// PricingPage.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import { useNavigate } from "react-router-dom";

// Pure Tailwind Toggle Switch (No library needed)
const ToggleSwitch = ({ isYearly, setIsYearly }) => {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={isYearly}
        onChange={() => setIsYearly(!isYearly)}
      />
      <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[var(--primary-color)] transition-colors duration-300"></div>
      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 peer-checked:translate-x-5"></div>
    </label>
  );
};

// Main Pricing Page
export default function PricingPage() {
  const [isYearly, setIsYearly] = useState(false);
  const navigate = useNavigate();

  const plans = [
    {
      name: "Starter",
      monthly: "₹999",
      yearly: "₹9,990",
      features: [
        "Up to 50 trips/month",
        "Basic LR/Bilty generation",
        "5 vehicles management",
        "Email support",
        "Mobile app access",
      ],
    },
    {
      name: "Pro",
      monthly: "₹2,999",
      yearly: "₹29,990",
      popular: true,
      features: [
        "Unlimited trips",
        "Advanced LR/Bilty generation",
        "Unlimited vehicles",
        "Fuel & expense tracking",
        "Priority support",
        "Custom reports",
        "API access",
      ],
    },
    {
      name: "Enterprise",
      monthly: "Custom",
      yearly: "Custom",
      features: [
        "Everything in Pro",
        "Dedicated account manager",
        "Custom integrations",
        "White-label options",
        "24/7 phone support",
        "On-premise deployment",
        "Training sessions",
      ],
    },
  ];

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;

    script.onload = () => {
      console.log("✅ Calendly script loaded");
    };

    script.onerror = () => {
      console.error("❌ Failed to load Calendly script");
    };

    document.body.appendChild(script);

    const handleEvent = (e) => {
      if (e?.data?.event === "calendly.event_scheduled") {
        console.log("✅ Meeting Scheduled:", e.data);
      }
    };

    window.addEventListener("message", handleEvent);
    return () => window.removeEventListener("message", handleEvent);
  }, []);

  // ✅ Popup handler with fallback retry
  const handleBookDemo = () => {
    console.log("👆 Book Demo clicked");

    const openPopup = () => {
      if (window.Calendly?.initPopupWidget) {
        window.Calendly.initPopupWidget({
          url: "https://calendly.com/triosoft-support/30min",
        });
        console.log("✅ Calendly Popup Opened");
        return true;
      }
      return false;
    };

    // ✅ Try opening immediately
    if (openPopup()) return;

    // ✅ Retry after delay if script isn't ready
    console.warn("⚠️ Calendly not ready, retrying...");
    setTimeout(() => {
      if (!openPopup()) {
        alert("Calendly is still loading. Please try again in a moment.");
      }
    }, 500);
  };

  const handleRouteContact = () => {
    navigate("/contact-us");
  };

  return (
    <main className="bg-gray-50 text-gray-900 min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="py-10 lg:py-24 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-[28px] lg:text-[48px] font-bold mb-5 leading-tight"
          >
            Simple, Transparent Pricing
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-[16px] lg:text-[18px] text-[var(--gray-text)] max-w-[100%] lg:max-w-[75%] mx-auto leading-relaxed mb-7"
          >
            Choose the perfect plan for your transport business. No hidden fees,
            cancel anytime.
          </motion.p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-16">
            <span
              className={`font-medium ${
                !isYearly ? "text-[var(--primary-color)]" : "text-gray-500"
              }`}
            >
              Monthly
            </span>
            <ToggleSwitch isYearly={isYearly} setIsYearly={setIsYearly} />
            <span
              className={`font-medium ${
                isYearly ? "text-[var(--primary-color)]" : "text-gray-500"
              }`}
            >
              Yearly
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                Save 17%
              </span>
            </span>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className={`relative bg-white rounded-2xl shadow-lg p-8 border-2 transition-all hover:shadow-2xl ${
                  plan.popular
                    ? "border-[var(--primary-color)]"
                    : "border-gray-200"
                }`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[var(--primary-color)] text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <h3 className="text-2xl text-[var(--black)] font-bold mb-4">
                    {plan.name}
                  </h3>

                  <div className="mb-6">
                    <span className="text-4xl text-[var(--black)] font-bold">
                      {isYearly ? plan.yearly : plan.monthly}
                    </span>
                    <span className="text-[var(--gray-text)] text-sm">
                      {isYearly ? "/year" : "/month"}
                    </span>
                  </div>

                  <ul className="space-y-3 mb-8 text-left">
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center text-[var(--gray-text)]"
                      >
                        <svg
                          className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    plan.popular
                      ? "theme-btn justify-center"
                      : "second_theme_btn justify-center"
                  }`}
                  onClick={
                    plan.monthly === "Custom"
                      ? handleRouteContact
                      : handleBookDemo
                  }
                >
                  {plan.monthly === "Custom" ? "Contact Sales" : "Book a Demo"}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
